"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronUp, ExternalLink, Film, ImageIcon, LogOut, MessageSquare, PenTool, Plus, Users } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTheme } from "next-themes";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/ui/logo";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { OrganizationSwitcher } from "./organization-switcher";
import type { Organization } from "@/lib/types";
interface CanvasSidebarProps {
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  className?: string;
  onNewCanvas?: () => void;
}
export function CanvasSidebar(t0) {
  const $ = _c(141);
  const {
    organizations,
    selectedOrganization,
    onSelectOrganization,
    className,
    onNewCanvas
  } = t0;
  let switcherOrganizations;
  let t1;
  if ($[0] !== organizations || $[1] !== selectedOrganization?.id) {
    switcherOrganizations = organizations.filter(_temp);
    t1 = switcherOrganizations.find(org_0 => org_0.id === selectedOrganization?.id) ?? null;
    $[0] = organizations;
    $[1] = selectedOrganization?.id;
    $[2] = switcherOrganizations;
    $[3] = t1;
  } else {
    switcherOrganizations = $[2];
    t1 = $[3];
  }
  const switcherSelectedOrganization = t1;
  const router = useRouter();
  const posthog = usePostHog();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  let t2;
  if ($[4] !== posthog || $[5] !== router || $[6] !== signOut) {
    t2 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://llm-playground.agenzo.com/login");
          }
        }
      });
    };
    $[4] = posthog;
    $[5] = router;
    $[6] = signOut;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const logout = t2;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t3;
  if ($[8] !== searchParams) {
    t3 = searchParams.get("orgId");
    $[8] = searchParams;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const orgIdParam = t3;
  let t4;
  if ($[10] !== orgIdParam) {
    t4 = path => orgIdParam ? `${path}?orgId=${orgIdParam}` : path;
    $[10] = orgIdParam;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const withOrg = t4;
  const {
    theme,
    setTheme,
    systemTheme
  } = useTheme();
  const currentTheme = theme === "system" ? systemTheme : theme;
  let t5;
  if ($[12] !== currentTheme || $[13] !== setTheme) {
    t5 = () => {
      setTheme(currentTheme === "dark" ? "light" : "dark");
    };
    $[12] = currentTheme;
    $[13] = setTheme;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const toggleTheme = t5;
  const isAuthenticated = !!user;
  if (isUserLoading) {
    let t6;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4"><Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Logo className="h-10 w-10" /><h1 className="text-xl font-semibold">LLM Gateway</h1><Badge>Canvas</Badge></Link></div></SidebarHeader>;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    let t7;
    if ($[16] !== className) {
      t7 = <Sidebar className={className}>{t6}</Sidebar>;
      $[16] = className;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    return t7;
  }
  if (!isAuthenticated) {
    let t6;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Logo className="h-10 w-10" /><h1 className="text-xl font-semibold">LLM Gateway</h1><Badge>Canvas</Badge></Link>;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    let t8;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="font-medium mb-2">Sign in required</div>;
      t8 = <p className="text-muted-foreground mb-3">Please sign in to use Canvas.</p>;
      $[19] = t7;
      $[20] = t8;
    } else {
      t7 = $[19];
      t8 = $[20];
    }
    let t9;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button>;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t6}<div className="w-full rounded-md border p-4 text-sm">{t7}{t8}<div className="flex items-center justify-end gap-2">{t9}<Button size="sm" variant="outline" asChild={true}><Link href="/signup">Create account</Link></Button></div></div></div></SidebarHeader>;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== className) {
      t11 = <Sidebar className={className}>{t10}</Sidebar>;
      $[23] = className;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    return t11;
  }
  const t6 = (className ?? "") + " max-md:hidden";
  let t7;
  if ($[25] !== withOrg) {
    t7 = withOrg("/");
    $[25] = withOrg;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t10;
  let t8;
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex aspect-square size-8 items-center justify-center"><Logo className="size-6" /></div>;
    t9 = <span className="text-lg font-bold tracking-tight">LLM Gateway</span>;
    t10 = <Badge variant="secondary" className="group-data-[collapsible=icon]:hidden">Chat</Badge>;
    $[27] = t10;
    $[28] = t8;
    $[29] = t9;
  } else {
    t10 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t11;
  if ($[30] !== t7) {
    t11 = <SidebarMenuItem><SidebarMenuButton size="lg" asChild={true} tooltip="LLM Gateway"><Link href={t7} prefetch={true}>{t8}{t9}{t10}</Link></SidebarMenuButton></SidebarMenuItem>;
    $[30] = t7;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Plus className="h-4 w-4" />;
    t13 = <span>New Canvas</span>;
    $[32] = t12;
    $[33] = t13;
  } else {
    t12 = $[32];
    t13 = $[33];
  }
  let t14;
  if ($[34] !== onNewCanvas) {
    t14 = <SidebarMenuItem><SidebarMenuButton onClick={onNewCanvas} tooltip="New Canvas" className="border border-border">{t12}{t13}</SidebarMenuButton></SidebarMenuItem>;
    $[34] = onNewCanvas;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = pathname === "/";
  let t16;
  if ($[36] !== withOrg) {
    t16 = withOrg("/");
    $[36] = withOrg;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <MessageSquare className="h-4 w-4" />;
    t18 = <span>Chat</span>;
    $[38] = t17;
    $[39] = t18;
  } else {
    t17 = $[38];
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t16) {
    t19 = <Link href={t16} prefetch={true}>{t17}{t18}</Link>;
    $[40] = t16;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t15 || $[43] !== t19) {
    t20 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Chat" isActive={t15}>{t19}</SidebarMenuButton></SidebarMenuItem>;
    $[42] = t15;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const t21 = pathname === "/group";
  let t22;
  if ($[45] !== withOrg) {
    t22 = withOrg("/group");
    $[45] = withOrg;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  let t24;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Users className="h-4 w-4" />;
    t24 = <span>Group Chat</span>;
    $[47] = t23;
    $[48] = t24;
  } else {
    t23 = $[47];
    t24 = $[48];
  }
  let t25;
  if ($[49] !== t22) {
    t25 = <Link href={t22} prefetch={true}>{t23}{t24}</Link>;
    $[49] = t22;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t21 || $[52] !== t25) {
    t26 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Group Chat" isActive={t21}>{t25}</SidebarMenuButton></SidebarMenuItem>;
    $[51] = t21;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  const t27 = pathname === "/image";
  let t28;
  if ($[54] !== withOrg) {
    t28 = withOrg("/image");
    $[54] = withOrg;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  let t30;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <ImageIcon className="h-4 w-4" />;
    t30 = <span>Image Studio</span>;
    $[56] = t29;
    $[57] = t30;
  } else {
    t29 = $[56];
    t30 = $[57];
  }
  let t31;
  if ($[58] !== t28) {
    t31 = <Link href={t28} prefetch={true}>{t29}{t30}</Link>;
    $[58] = t28;
    $[59] = t31;
  } else {
    t31 = $[59];
  }
  let t32;
  if ($[60] !== t27 || $[61] !== t31) {
    t32 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Image Studio" isActive={t27}>{t31}</SidebarMenuButton></SidebarMenuItem>;
    $[60] = t27;
    $[61] = t31;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  const t33 = pathname === "/video";
  let t34;
  if ($[63] !== withOrg) {
    t34 = withOrg("/video");
    $[63] = withOrg;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  let t36;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Film className="h-4 w-4" />;
    t36 = <span>Video Studio</span>;
    $[65] = t35;
    $[66] = t36;
  } else {
    t35 = $[65];
    t36 = $[66];
  }
  let t37;
  if ($[67] !== t34) {
    t37 = <Link href={t34} prefetch={true}>{t35}{t36}</Link>;
    $[67] = t34;
    $[68] = t37;
  } else {
    t37 = $[68];
  }
  let t38;
  if ($[69] !== t33 || $[70] !== t37) {
    t38 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Video Studio" isActive={t33}>{t37}</SidebarMenuButton></SidebarMenuItem>;
    $[69] = t33;
    $[70] = t37;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  const t39 = pathname === "/canvas";
  let t40;
  if ($[72] !== withOrg) {
    t40 = withOrg("/canvas");
    $[72] = withOrg;
    $[73] = t40;
  } else {
    t40 = $[73];
  }
  let t41;
  let t42;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <PenTool className="h-4 w-4" />;
    t42 = <span>Canvas</span>;
    $[74] = t41;
    $[75] = t42;
  } else {
    t41 = $[74];
    t42 = $[75];
  }
  let t43;
  if ($[76] !== t40) {
    t43 = <Link href={t40} prefetch={true}>{t41}{t42}</Link>;
    $[76] = t40;
    $[77] = t43;
  } else {
    t43 = $[77];
  }
  let t44;
  if ($[78] !== t39 || $[79] !== t43) {
    t44 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip="Canvas" isActive={t39}>{t43}</SidebarMenuButton></SidebarMenuItem>;
    $[78] = t39;
    $[79] = t43;
    $[80] = t44;
  } else {
    t44 = $[80];
  }
  let t45;
  if ($[81] !== t11 || $[82] !== t14 || $[83] !== t20 || $[84] !== t26 || $[85] !== t32 || $[86] !== t38 || $[87] !== t44) {
    t45 = <SidebarHeader><SidebarMenu>{t11}{t14}{t20}{t26}{t32}{t38}{t44}</SidebarMenu></SidebarHeader>;
    $[81] = t11;
    $[82] = t14;
    $[83] = t20;
    $[84] = t26;
    $[85] = t32;
    $[86] = t38;
    $[87] = t44;
    $[88] = t45;
  } else {
    t45 = $[88];
  }
  let t46;
  if ($[89] !== onSelectOrganization || $[90] !== switcherOrganizations || $[91] !== switcherSelectedOrganization) {
    t46 = switcherOrganizations.length > 0 ? <SidebarMenu className="group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={switcherOrganizations} selectedOrganization={switcherSelectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[89] = onSelectOrganization;
    $[90] = switcherOrganizations;
    $[91] = switcherSelectedOrganization;
    $[92] = t46;
  } else {
    t46 = $[92];
  }
  let t47;
  if ($[93] !== t46) {
    t47 = <SidebarContent className="px-2 py-4">{t46}</SidebarContent>;
    $[93] = t46;
    $[94] = t47;
  } else {
    t47 = $[94];
  }
  const t48 = switcherSelectedOrganization ?? organization;
  const t49 = !switcherSelectedOrganization;
  let t50;
  if ($[95] !== isOrgLoading || $[96] !== t48 || $[97] !== t49) {
    t50 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t48} isLoading={isOrgLoading} isChatPlanOrg={t49} /></div>;
    $[95] = isOrgLoading;
    $[96] = t48;
    $[97] = t49;
    $[98] = t50;
  } else {
    t50 = $[98];
  }
  const t51 = user?.name ?? "User";
  let t52;
  if ($[99] !== user?.name) {
    t52 = user?.name?.split(" ").map(_temp2).join("").toUpperCase().slice(0, 2) ?? "U";
    $[99] = user?.name;
    $[100] = t52;
  } else {
    t52 = $[100];
  }
  let t53;
  if ($[101] !== t52) {
    t53 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t52}</span></div>;
    $[101] = t52;
    $[102] = t53;
  } else {
    t53 = $[102];
  }
  const t54 = user?.name;
  let t55;
  if ($[103] !== t54) {
    t55 = <span className="truncate font-semibold">{t54}</span>;
    $[103] = t54;
    $[104] = t55;
  } else {
    t55 = $[104];
  }
  const t56 = user?.email;
  let t57;
  if ($[105] !== t56) {
    t57 = <span className="truncate text-xs text-muted-foreground">{t56}</span>;
    $[105] = t56;
    $[106] = t57;
  } else {
    t57 = $[106];
  }
  let t58;
  if ($[107] !== t55 || $[108] !== t57) {
    t58 = <div className="grid flex-1 text-left text-sm leading-tight">{t55}{t57}</div>;
    $[107] = t55;
    $[108] = t57;
    $[109] = t58;
  } else {
    t58 = $[109];
  }
  let t59;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[110] = t59;
  } else {
    t59 = $[110];
  }
  let t60;
  if ($[111] !== t51 || $[112] !== t53 || $[113] !== t58) {
    t60 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t51} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t53}{t58}{t59}</SidebarMenuButton></DropdownMenuTrigger>;
    $[111] = t51;
    $[112] = t53;
    $[113] = t58;
    $[114] = t60;
  } else {
    t60 = $[114];
  }
  let t61;
  let t62;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llm-gateway.agenzo.com/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t62 = <DropdownMenuSeparator />;
    $[115] = t61;
    $[116] = t62;
  } else {
    t61 = $[115];
    t62 = $[116];
  }
  let t63;
  if ($[117] !== toggleTheme) {
    t63 = event => {
      event.preventDefault();
      toggleTheme();
    };
    $[117] = toggleTheme;
    $[118] = t63;
  } else {
    t63 = $[118];
  }
  let t64;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <span>Theme</span>;
    $[119] = t64;
  } else {
    t64 = $[119];
  }
  let t65;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <div onClick={_temp3} onKeyDown={_temp4}><ThemeToggle className="shrink-0" size="compact" /></div>;
    $[120] = t65;
  } else {
    t65 = $[120];
  }
  let t66;
  if ($[121] !== t63) {
    t66 = <DropdownMenuItem className="justify-between gap-3" onSelect={t63}>{t64}{t65}</DropdownMenuItem>;
    $[121] = t63;
    $[122] = t66;
  } else {
    t66 = $[122];
  }
  let t67;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = <DropdownMenuSeparator />;
    $[123] = t67;
  } else {
    t67 = $[123];
  }
  let t68;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <LogOut className="mr-2 h-4 w-4" />;
    $[124] = t68;
  } else {
    t68 = $[124];
  }
  let t69;
  if ($[125] !== logout) {
    t69 = <DropdownMenuItem onClick={logout}>{t68}Log out</DropdownMenuItem>;
    $[125] = logout;
    $[126] = t69;
  } else {
    t69 = $[126];
  }
  let t70;
  if ($[127] !== t66 || $[128] !== t69) {
    t70 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t61}{t62}{t66}{t67}{t69}</DropdownMenuContent>;
    $[127] = t66;
    $[128] = t69;
    $[129] = t70;
  } else {
    t70 = $[129];
  }
  let t71;
  if ($[130] !== t60 || $[131] !== t70) {
    t71 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t60}{t70}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[130] = t60;
    $[131] = t70;
    $[132] = t71;
  } else {
    t71 = $[132];
  }
  let t72;
  if ($[133] !== t50 || $[134] !== t71) {
    t72 = <SidebarFooter>{t50}{t71}</SidebarFooter>;
    $[133] = t50;
    $[134] = t71;
    $[135] = t72;
  } else {
    t72 = $[135];
  }
  let t73;
  if ($[136] !== t45 || $[137] !== t47 || $[138] !== t6 || $[139] !== t72) {
    t73 = <Sidebar collapsible="icon" className={t6}>{t45}{t47}{t72}</Sidebar>;
    $[136] = t45;
    $[137] = t47;
    $[138] = t6;
    $[139] = t72;
    $[140] = t73;
  } else {
    t73 = $[140];
  }
  return t73;
}
function _temp4(event_1) {
  return event_1.stopPropagation();
}
function _temp3(event_0) {
  return event_0.stopPropagation();
}
function _temp2(n) {
  return n[0];
}
function _temp(org) {
  return !org.isPersonal && !org.isChat;
}